import { create } from "zustand";
import { profileService, ProfileResponse } from "@/services/profileService";

interface ProfileState {
  profileData: ProfileResponse | null;
  isLoading: boolean;
  error: string | null;
  fetchProfile: (email: string, legalEntityId?: string | null) => Promise<void>;
  refreshProfile: (email: string, legalEntityId?: string | null) => Promise<void>;
  /** After Integrations (or any screen) fetches /profile, push it here so Reach Out et al. stay in sync. */
  applyProfileData: (data: ProfileResponse) => void;
  clearProfile: () => void;
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  profileData: null,
  isLoading: false,
  error: null,

  fetchProfile: async (email: string, legalEntityId?: string | null) => {
    const current = get().profileData;
    const requestedEmail = email ? email.trim().toLowerCase() : "";
    const currentEmail = current?.email?.trim().toLowerCase();
    if (current && currentEmail === requestedEmail && !get().error) return;

    // Never expose a previous account's profile while the newly authenticated
    // user's profile is loading.
    set({
      profileData: currentEmail && currentEmail !== requestedEmail ? null : current,
      isLoading: true,
      error: null,
    });
    try {
      const data = await profileService.get(email, legalEntityId);
      set({ profileData: data, isLoading: false });
    } catch (err: any) {
      set({ error: err.message || "Failed to fetch profile", isLoading: false });
    }
  },

  refreshProfile: async (email: string, legalEntityId?: string | null) => {
    set({ isLoading: true, error: null });
    try {
      const data = await profileService.refresh(email, legalEntityId);
      set({ profileData: data, isLoading: false });
    } catch (err: any) {
      set({ error: err.message || "Failed to refresh profile", isLoading: false });
    }
  },

  applyProfileData: (data) => set({ profileData: data, isLoading: false, error: null }),

  clearProfile: () => set({ profileData: null, error: null, isLoading: false }),
}));
